// Mirrors the backend RiskAreaScaleMapping. Both sides have to agree on what a proposed re-base rule means:
// the wizard validates and previews with these, and the approved arrays are what the backend then maps with.

export interface ScaleMappingCollapse {
    // 1-based source levels that all land on the same target level.
    sourceLevels: number[];
    targetLevel: number;
}

// Preserves both endpoints and spreads the levels in between evenly. Only a prefill: every row stays editable.
export function proportionalMapping(fromScale: number, toScale: number): number[] {
    return Array.from({ length: fromScale }, (_, index) => 1 + roundHalfToEven((index * (toScale - 1)) / (fromScale - 1)));
}

export function isValidMapping(mapping: number[], fromScale: number, toScale: number): boolean {
    if (mapping.length !== fromScale) return false;
    if (mapping.some((level) => !Number.isInteger(level) || level < 1 || level > toScale)) return false;

    // A mapping that sends a worse level somewhere milder than a level below it re-orders severity, which is
    // never a legitimate re-base.
    return mapping.every((level, index) => index === 0 || level >= mapping[index - 1]);
}

// Every target level that more than one source level lands on. A collapse is legitimate when shrinking the
// scale, but it merges distinctions the customer previously drew, so it is called out rather than assumed.
export function findCollapses(mapping: number[]): Array<ScaleMappingCollapse> {
    const sourceLevelsByTarget = new Map<number, number[]>();

    mapping.forEach((targetLevel, index) => {
        sourceLevelsByTarget.set(targetLevel, [...(sourceLevelsByTarget.get(targetLevel) ?? []), index + 1]);
    });

    return [...sourceLevelsByTarget.entries()]
        .filter(([, sourceLevels]) => sourceLevels.length > 1)
        .map(([targetLevel, sourceLevels]) => ({ targetLevel, sourceLevels }));
}

// .NET's Math.Round rounds halves to even by default, and the backend's proportional prefill relies on it to
// collapse the pair nearest each end first (5 -> 3 gives 1,1,2,3,3). JavaScript rounds halves up, so matching
// the domain has to be explicit rather than inherited from the language.
function roundHalfToEven(value: number): number {
    const lower = Math.floor(value);
    const remainder = value - lower;

    if (remainder > 0.5) return lower + 1;
    if (remainder < 0.5) return lower;

    return lower % 2 === 0 ? lower : lower + 1;
}
